import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Check, GitMerge, RefreshCw, Search, Tag, Users, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  apiErrorMessage, getObligorMasters, proposeObligorAlias, proposeObligorMerge, reviewObligorAlias, reviewObligorMerge,
} from '../api/abfApi';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../components/ActionFeedback';
import { Header, SmallButton } from '../onboarding/review/contractReviewStyles';
import { INK } from '../onboarding/review/reviewStyles';
import { ABF_HOME_PATH } from '../routes';
import type { ObligorIdentityBasis, ObligorMaster, ObligorMasterList } from '../types/obligors';
import { Shell, money } from './boardShared';

/**
 * Item G (V697) — the curated obligor master.
 *
 * One row per legal obligor across every facility's latest released tape: its LEI when known, the
 * names the servicers use for it, which facilities carry it, the balance on those tapes, and how
 * each row was matched at release (LEI, approved alias, normalised name, or created from the tape).
 * Curation is maker-checker: anyone who works the collateral proposes a merge or an alias; a
 * different checker approves. The server refuses the proposer under the tenant's four-eyes policy.
 */

const CHECKER_ROLES = new Set(['RISK', 'RISK_MANAGER', 'COMPLIANCE', 'COMPLIANCE_OFFICER', 'OPERATIONS', 'OPERATIONS_ANALYST',
  'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
const MAKER_ROLES = new Set(Array.from(CHECKER_ROLES).concat(['PM', 'PORTFOLIO_MANAGER']));

const Kpis = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 0 0 16px;
  > div { padding: 14px 16px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.raised}; }
  span { display: block; font-size: 12px; color: ${INK.muted}; letter-spacing: .02em; }
  strong { display: block; margin-top: 4px; font-size: 22px; font-weight: 700; color: ${INK.strong}; font-variant-numeric: tabular-nums; }
  small { display: block; margin-top: 3px; font-size: 12px; color: ${INK.muted}; }
  .warn strong { color: #edcc82; }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;
const Bar = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: 0 0 14px;
  .search { display: flex; align-items: center; gap: 8px; flex: 1 1 280px; padding: 0 12px; min-height: 38px; border: 1px solid ${INK.lineStrong}; border-radius: 9px; background: ${INK.panel}; color: ${INK.muted}; }
  .search input { flex: 1; border: 0; background: transparent; color: ${INK.body}; font: inherit; font-size: 14px; outline: none; }
  .seg { display: inline-flex; border: 1px solid ${INK.lineStrong}; border-radius: 9px; overflow: hidden; }
  .seg button { min-height: 36px; padding: 0 13px; border: 0; background: transparent; color: ${INK.soft}; font: inherit; font-size: 13px; cursor: pointer; }
  .seg button + button { border-left: 1px solid ${INK.lineStrong}; }
  .seg button[aria-pressed='true'] { background: #12273c; color: ${INK.strong}; box-shadow: inset 0 -2px #67c6f4; }
`;
const Pending = styled.section`
  margin: 0 0 14px; padding: 12px 16px; border: 1px solid #5a4a1f; border-radius: 10px; background: #1a1708;
  h2 { margin: 0 0 8px; font-size: 13px; letter-spacing: .06em; text-transform: uppercase; color: #edcc82; }
  .item { display: flex; flex-wrap: wrap; gap: 8px 14px; align-items: center; padding: 6px 0; font-size: 13px; color: ${INK.body}; }
  .item + .item { border-top: 1px solid ${INK.line}; }
  .item small { color: ${INK.muted}; }
  .grow { flex: 1 1 320px; }
`;
const Rows = styled.div`
  border: 1px solid ${INK.line}; border-radius: 11px; background: ${INK.raised}; overflow: hidden;
  .row { display: grid; grid-template-columns: minmax(0, 2.4fr) minmax(0, 1.3fr) minmax(0, 1.6fr) minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 1.5fr); gap: 14px; padding: 11px 16px; align-items: start; }
  .row + .row { border-top: 1px solid ${INK.line}; }
  .head { padding-top: 8px; padding-bottom: 8px; font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: ${INK.muted}; background: ${INK.panel}; }
  .name { font-weight: 600; color: ${INK.strong}; font-size: 14px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .meta { font-size: 12px; color: ${INK.muted}; margin-top: 3px; }
  .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: ${INK.body}; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .num strong { font-size: 15px; color: ${INK.strong}; }
  .alias { display: inline-flex; align-items: center; gap: 4px; margin: 2px 6px 2px 0; padding: 1px 7px; border: 1px solid ${INK.lineStrong}; border-radius: 999px; font-size: 12px; color: ${INK.body}; }
  .alias.pending { border-style: dashed; color: #edcc82; }
  .basis { display: inline-flex; gap: 8px; flex-wrap: wrap; font-size: 12px; color: ${INK.body}; }
  .basis b { color: ${INK.strong}; font-weight: 600; }
  .actions { display: flex; flex-wrap: wrap; gap: 6px; }
  .actions input, .actions select { padding: 4px 7px; border: 1px solid ${INK.lineStrong}; border-radius: 7px; background: ${INK.well}; color: ${INK.body}; font: inherit; font-size: 12px; min-width: 0; }
  .actions button { display: inline-flex; align-items: center; gap: 4px; padding: 4px 9px; border: 1px solid ${INK.lineStrong}; border-radius: 7px; background: ${INK.well}; color: ${INK.body}; font: inherit; font-size: 12px; cursor: pointer; }
  .actions button:disabled { opacity: .5; cursor: default; }
  @media (max-width: 1100px) { .row { grid-template-columns: 1fr 1fr; } .head { display: none; } .num { text-align: left; } }
`;

type Filter = 'ALL' | 'LEI' | 'NAME' | 'MULTI';
const basisTone = (b: ObligorIdentityBasis): StatusTone => b === 'LEI' ? 'done' : b === 'CURATED' ? 'progress' : 'neutral';
const basisLabel = (b: ObligorIdentityBasis) => b === 'LEI' ? 'LEI' : b === 'CURATED' ? 'curated' : 'name only';

const ObligorsPage: React.FC = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toUpperCase();
  const isMaker = MAKER_ROLES.has(role);
  const isChecker = CHECKER_ROLES.has(role);

  const [data, setData] = useState<ObligorMasterList | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getObligorMasters()); }
    catch (error) { setFeedback({ tone: 'error', message: apiErrorMessage(error, 'The obligor master could not be loaded.') }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const act = useCallback(async (key: string, run: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try { await run(); setFeedback({ tone: 'success', message: done }); await load(); }
    catch (error) { setFeedback({ tone: 'error', message: apiErrorMessage(error, 'The change was not applied.') }); }
    finally { setBusy(null); }
  }, [load]);
  // Stable across renders, so a keystroke in the search box does not redraw every row (ObligorRow is memoised).
  const proposeAlias = useCallback((master: ObligorMaster, alias: string) =>
    act(`alias-${master.id}`, () => proposeObligorAlias(master.id, alias), `Alias proposed for ${master.canonicalName}; a checker approves it.`), [act]);
  const reviewAlias = useCallback((aliasId: number, approve: boolean) =>
    void act(`alias-${aliasId}`, () => reviewObligorAlias(aliasId, approve, ''), approve ? 'Alias approved.' : 'Alias rejected.'), [act]);
  const proposeMerge = useCallback((master: ObligorMaster, targetId: number) =>
    act(`merge-${master.id}`, () => proposeObligorMerge(master.id, targetId, ''), 'Merge proposed; a checker approves it.'), [act]);

  const masters = useMemo(() => data?.masters ?? [], [data]);
  const q = query.trim().toLowerCase();
  const visible = masters.filter(m => (filter === 'ALL'
      || (filter === 'LEI' && !!m.lei)
      || (filter === 'NAME' && m.identityBasis === 'NAME')
      || (filter === 'MULTI' && m.facilities > 1))
    && (!q || [m.canonicalName, m.lei, m.identifiers, m.facilityCodes, ...m.aliases.map(a => a.alias)]
      .some(v => String(v || '').toLowerCase().includes(q))));
  const t = data?.totals;
  const crossFacility = masters.filter(m => m.facilities > 1).length;
  const nameOnly = masters.filter(m => m.identityBasis === 'NAME').length;

  return <Shell>
    <Header>
      <Link className="back" to={ABF_HOME_PATH}><ArrowLeft size={14} /> ABF home</Link>
      <div className="heading">
        <div>
          <div className="eyebrow">Asset-based finance</div>
          <h1><Users size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />Obligors</h1>
          <div className="meta">
            <span>Business date {data?.businessDate ?? '…'}</span>
            {t && <span>{t.masters} obligors on the latest released tapes</span>}
            <span>{isChecker ? 'Checker: you approve merges and aliases proposed by someone else' : isMaker ? 'Maker: you propose merges and aliases; a checker approves' : 'Read only'}</span>
          </div>
        </div>
        <div className="actions"><SmallButton type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}</SmallButton></div>
      </div>
    </Header>

    {t && <Kpis aria-label="Obligor totals">
      <div><span>Anchored on an LEI</span><strong>{t.withLei} of {t.masters}</strong><small>identity from the tape or a curator, not spelling</small></div>
      <div className={nameOnly ? 'warn' : undefined}><span>Name only</span><strong>{nameOnly}</strong><small>matched or created on spelling alone</small></div>
      <div><span>On more than one facility</span><strong>{crossFacility}</strong><small>the cross-facility rows the look-through merges</small></div>
      <div className={t.pendingMerges + t.pendingAliases ? 'warn' : undefined}><span>Awaiting a checker</span><strong>{t.pendingMerges + t.pendingAliases}</strong><small>{t.pendingMerges} merge{t.pendingMerges === 1 ? '' : 's'} · {t.pendingAliases} alias{t.pendingAliases === 1 ? '' : 'es'}</small></div>
    </Kpis>}

    {data && data.pendingMerges.length > 0 && <Pending aria-label="Merges awaiting review">
      <h2><GitMerge size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />Merges awaiting a checker</h2>
      {data.pendingMerges.map(r => <div className="item" key={r.id}>
        <div className="grow"><strong>{r.sourceName}</strong> → <strong>{r.targetName}</strong>{r.reason && <> · {r.reason}</>}<br /><small>proposed by {r.proposedBy || 'unknown'}{r.proposedAt ? ` · ${new Date(r.proposedAt).toLocaleString()}` : ''}</small></div>
        {isChecker && <div className="actions" style={{ display: 'flex', gap: 6 }}>
          <SmallButton type="button" disabled={busy !== null || r.proposedBy === user?.username} title={r.proposedBy === user?.username ? 'You proposed this merge; a different checker approves it' : undefined}
            onClick={() => void act(`merge-${r.id}`, () => reviewObligorMerge(r.id, true, ''), `Merged ${r.sourceName} into ${r.targetName}.`)}><Check size={13} /> Approve</SmallButton>
          <SmallButton type="button" disabled={busy !== null} onClick={() => void act(`merge-${r.id}`, () => reviewObligorMerge(r.id, false, ''), 'Merge rejected.')}><X size={13} /> Reject</SmallButton>
        </div>}
      </div>)}
    </Pending>}

    <Bar>
      <label className="search"><Search size={15} /><input aria-label="Search obligors" placeholder="Search name, LEI, servicer id, alias or facility" value={query} onChange={e => setQuery(e.target.value)} /></label>
      <div className="seg" role="group" aria-label="Identity basis">
        {(['ALL', 'LEI', 'NAME', 'MULTI'] as Filter[]).map(f => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
          {f === 'ALL' ? 'All' : f === 'LEI' ? 'With LEI' : f === 'NAME' ? 'Name only' : 'Cross-facility'}
        </button>)}
      </div>
    </Bar>

    <Rows>
      <div className="row head"><div>Obligor</div><div>LEI</div><div>Aliases</div><div className="num">Exposure</div><div>Facilities</div><div>Matched at release</div></div>
      {visible.map(m => <ObligorRow key={m.id} m={m} masters={masters} isMaker={isMaker} isChecker={isChecker} busy={busy} me={user?.username}
        onProposeAlias={proposeAlias} onReviewAlias={reviewAlias} onProposeMerge={proposeMerge} />)}
      {!visible.length && !loading && <div className="row" style={{ display: 'block', textAlign: 'center', color: INK.muted }}>
        {masters.length ? 'No obligor matches.' : 'No released tape has been resolved yet. Obligors appear here when a tape is released after V697.'}
      </div>}
    </Rows>
    {data && <p style={{ margin: '12px 2px 0', fontSize: 12, color: INK.muted }}>{data.basis}</p>}
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Shell>;
};

interface RowProps {
  m: ObligorMaster; masters: ObligorMaster[]; isMaker: boolean; isChecker: boolean; busy: string | null; me?: string;
  onProposeAlias: (master: ObligorMaster, alias: string) => Promise<void>;
  onReviewAlias: (aliasId: number, approve: boolean) => void;
  onProposeMerge: (master: ObligorMaster, targetId: number) => Promise<void>;
}

// Prod 2026-10-31: every row used to carry a "Merge into…" select listing every other master, so 769 masters drew
// about 591,000 <option> elements and each filter change or keystroke froze the browser for seconds. The picker is
// now built for one row, when that row's "Merge…" is opened, and the alias draft lives in the row.
const ObligorRow = React.memo(function ObligorRow({ m, masters, isMaker, isChecker, busy, me, onProposeAlias, onReviewAlias, onProposeMerge }: RowProps) {
  const counts = m.matchBasisCounts || { LEI: 0, ALIAS: 0, NAME: 0, CREATED: 0 };
  const [aliasDraft, setAliasDraft] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const mergeCandidates = useMemo(() => merging
    ? masters.filter(x => x.id !== m.id && (!x.lei || !m.lei || x.lei === m.lei))
    : [], [merging, masters, m.id, m.lei]);
  return <div className="row" data-testid={`obligor-${m.id}`}>
    <div>
      <div className="name">{m.canonicalName} <StatusChip tone={basisTone(m.identityBasis)} label={basisLabel(m.identityBasis)} size="sm" /></div>
      <div className="meta">{m.identifiers ? `Servicer id ${m.identifiers}` : 'No servicer id'}{m.mergedIn ? ` · ${m.mergedIn} merged in` : ''}{m.source !== 'TAPE' ? ` · ${m.source.toLowerCase()}` : ''}</div>
    </div>
    <div>{m.lei ? <span className="mono">{m.lei}</span> : <span style={{ color: INK.muted }}>—</span>}</div>
    <div>
      {m.aliases.map(a => <span key={a.id} className={`alias${a.status === 'PROPOSED' ? ' pending' : ''}`} title={a.status === 'PROPOSED' ? `Proposed by ${a.proposedBy || 'unknown'}` : `Approved by ${a.approvedBy || 'unknown'}`}>
        <Tag size={11} /> {a.alias}
        {a.status === 'PROPOSED' && isChecker && <>
          <button type="button" aria-label={`Approve alias ${a.alias}`} disabled={busy !== null || a.proposedBy === me} title={a.proposedBy === me ? 'You proposed this alias; a different checker approves it' : 'Approve'} onClick={() => onReviewAlias(a.id, true)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}><Check size={12} /></button>
          <button type="button" aria-label={`Reject alias ${a.alias}`} disabled={busy !== null} onClick={() => onReviewAlias(a.id, false)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
        </>}
      </span>)}
      {!m.aliases.length && <span style={{ color: INK.muted, fontSize: 12 }}>none</span>}
      {isMaker && <div className="actions" style={{ marginTop: 6 }}>
        <input aria-label={`New alias for ${m.canonicalName}`} placeholder="Add a name this obligor goes by" value={aliasDraft} onChange={e => setAliasDraft(e.target.value)} style={{ flex: '1 1 160px' }} />
        <button type="button" disabled={busy !== null || !aliasDraft.trim()} onClick={() => void onProposeAlias(m, aliasDraft.trim()).then(() => setAliasDraft(''))}><Tag size={12} /> Propose</button>
      </div>}
    </div>
    <div className="num"><strong>{money(m.balance)}</strong><div className="meta">{money(m.eligibleBalance)} eligible · {m.rows} loan{m.rows === 1 ? '' : 's'}</div></div>
    <div><span style={{ color: INK.strong }}>{m.facilities}</span><div className="meta">{m.facilityCodes || '—'}</div></div>
    <div>
      <div className="basis">
        {(['LEI', 'ALIAS', 'NAME', 'CREATED'] as const).filter(k => Number(counts[k]) > 0).map(k => <span key={k}><b>{counts[k]}</b> {k.toLowerCase()}</span>)}
        {!Object.values(counts).some(v => Number(v) > 0) && <span style={{ color: INK.muted }}>no rows</span>}
      </div>
      {isMaker && !merging && <div className="actions" style={{ marginTop: 6 }}>
        <button type="button" aria-label={`Merge ${m.canonicalName} into another obligor`} disabled={busy !== null} onClick={() => setMerging(true)}><GitMerge size={12} /> Merge…</button>
      </div>}
      {isMaker && merging && <div className="actions" style={{ marginTop: 6 }}>
        <select aria-label={`Merge ${m.canonicalName} into`} value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} style={{ flex: '1 1 140px' }}>
          <option value="">Merge into…</option>
          {mergeCandidates.map(x => <option key={x.id} value={x.id}>{x.canonicalName}{x.lei ? ` · ${x.lei}` : ''}</option>)}
        </select>
        <button type="button" disabled={busy !== null || !mergeTarget}
          onClick={() => void onProposeMerge(m, Number(mergeTarget)).then(() => { setMerging(false); setMergeTarget(''); })}><GitMerge size={12} /> Propose</button>
        <button type="button" disabled={busy !== null} onClick={() => { setMerging(false); setMergeTarget(''); }}><X size={12} /> Cancel</button>
      </div>}
    </div>
  </div>;
});

export default ObligorsPage;
